import { Shield, HeartPulse, TrendingUp, Building2 } from "lucide-react";
import useDocTitle from "../lib/useDocTitle";

export default function About() {
  useDocTitle(
    "About · Lulo Software",
    "Lulo Software is a B2B software company building decision-support tools for professionals across e-commerce compliance, skincare reference, and market research. Registered in Wyoming, USA.",
    "About Lulo Software, Wyoming LLC, B2B SaaS company, decision-support software, founder Kalyan Chakravarthy",
    "/about",
  );

  return (
    <div data-testid="about-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20 max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">About us</p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-semibold tracking-tight text-[#0A2540]">
            About Lulo Software
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-8">
            Lulo Software is a B2B software company building decision-support
            tools for professionals. Our portfolio includes{" "}
            <strong className="font-semibold text-slate-900">Appeal Architect</strong>{" "}
            (e-commerce compliance workflow software, available),{" "}
            <strong className="font-semibold text-slate-900">BlendSafe</strong>{" "}
            (reference software for skincare professionals, in development), and{" "}
            <strong className="font-semibold text-slate-900">PrimePillar</strong>{" "}
            (market research software for product teams, in development).
            Registered in Wyoming, USA. Operations in Hyderabad, India.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="lulo-container py-16 lg:py-20 max-w-4xl">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Our portfolio spans three areas</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "Platform Compliance",
                body: "Diagnostic and workflow software for sellers managing marketplace compliance requirements across e-commerce platforms.",
              },
              {
                icon: HeartPulse,
                title: "Skincare Reference",
                body: "Reference software for skincare professionals to look up ingredient and medication compatibility data during client consultations.",
              },
              {
                icon: TrendingUp,
                title: "Market Research",
                body: "Software that aggregates public discussion data to help product teams identify and prioritize customer problems.",
              },
            ].map((a) => (
              <div key={a.title} className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
                  <a.icon size={20} />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#0A2540]">{a.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-6">{a.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 lulo-prose">
            <h2>Our approach</h2>
            <p>We build software with three commitments:</p>
            <p>
              <strong>Privacy-first architecture.</strong> We do not request platform
              credentials, do not automate actions against user accounts, and minimize data
              collection across all our products.
            </p>
            <p>
              <strong>Decision-support, not decision-replacement.</strong> Our tools provide
              structured information, evidence validation, and policy-cited guidance. They do
              not make decisions for users or warrant specific outcomes — both of which we believe
              are inappropriate for high-stakes operational and healthcare contexts.
            </p>
            <p>
              <strong>Domain expertise integration.</strong> Our team combines software
              engineering, e-commerce operations, and pharmaceutical safety expertise to
              ensure our products reflect real-world operational reality.
            </p>

            <h2>Company information</h2>
            <p>
              Lulo Software LLC is registered in Wyoming, USA, with operations
              in Hyderabad, India. Registered Office: 30 N Gould St, Ste R,
              Sheridan, WY 82801.
            </p>
          </div>

          <div className="mt-12 rounded-xl border border-slate-200 bg-[#F9FAFB] p-6 flex items-start gap-4">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white ring-1 ring-slate-200 text-[#0A2540]">
              <Building2 size={20} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0A2540]">Contact</p>
              <p className="mt-1 text-sm text-slate-600">
                <a className="text-[#2563EB] hover:text-[#1d4ed8]" href="mailto:support@lulosoftware.com">support@lulosoftware.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
